import { useEffect, useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import { useNavigate } from 'react-router-dom';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { UsersCardView } from './users-cardview';
import { CardViewDataStateChangeEvent } from './organisations-cardview';
import { LinkCell } from '../components/grid';
import HelpSupport from '../components/help-support';
import RoleBasedButton from '../components/role-based-button';
import { Permission } from '../types/permission';
import { useGetAllUsers } from '../hooks/services/use-users';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'username', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const UsersGrid = () => {
  const navigate = useNavigate();
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');

  const { data, isLoading, mutate } = useGetAllUsers(
    toDataSourceRequestString(dataState),
    'organisation'
  );

  const LinkedNameCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props}></LinkCell>
  );

  const StatusColoring = (props: GridCellProps) => {
    const status = props.dataItem.status;
    return (
      <td
        className={`k-table-td ${status === 'Enabled' ? 'text-success' : 'text-muted'}`}>
        <span>{status}</span>
      </td>
    );
  };

  const onAddUser = () => {
    navigate('/users/invite-user');
  };

  const gridExportRef = useRef<ExcelExport | null>(null);

  useEffect(() => {
    mutate();
  }, [dataState]);

  return (
    <>
      <Toolbar title={'Users'}>
        <HelpSupport
          title="My organisation Members guide"
          url="https://forum.catalystglobal.com/t/4203"
        />
        <RoleBasedButton
          requiredRoles={[Permission['OrgAdmin.Members']]}
          label={'Add new'}
          onClick={onAddUser}
        />
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search users'}
          columnsToSearch={['username', 'fullName', 'preferredName']}
          showCardMode={true}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={'OrganisationMembers.xlsx'}>
          <ExcelExportColumn
            field="username"
            title="Email address"
            width={350}
          />
          <ExcelExportColumn field="fullName" title="Name" width={300} />
          <ExcelExportColumn field="country" title="Country" width={200} />
          <ExcelExportColumn field="phoneNumber" title="Phone" width={200} />
          <ExcelExportColumn field="specialRole" title="Role" width={200} />
          <ExcelExportColumn
            field="department"
            title="Department"
            width={200}
          />
          <ExcelExportColumn field="status" title="Status" width={250} />
        </ExcelExport>

        {gridMode === 'grid' && (
          <Grid
            id={'grid-user'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'username'}
              filter={'text'}
              title={'Username'}
              columnMenu={ColumnMenu}
              cells={{ data: LinkedNameCell }}
            />
            <Column field={'fullName'} title={'Name'} columnMenu={ColumnMenu} />
            <Column
              field={'country'}
              title={'Country'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'phoneNumber'}
              title={'Phone'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'specialRole'}
              title={'Role'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'department'}
              title={'Department'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'status'}
              title={'Status'}
              columnMenu={ColumnMenu}
              cells={{ data: StatusColoring }}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        )}
        {gridMode === 'card' && (
          <UsersCardView
            data={data?.data ?? []}
            onUsernameClick={(id: number) => navigate(`/users/${id}`)}
            total={data?.total}
            {...dataState}
            onDataStateChange={(e: CardViewDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
          />
        )}
      </div>
    </>
  );
};
export default UsersGrid;
